import React, {useState} from 'react';
import {ActivityIndicator, Pressable, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import Text from '../../../components/ui-controls/Text';
import {useStores} from '../../../stores/use-stores';

/**
 * DIS-1608: the second exit from a blocked checkout. The button says what to add;
 * this says they can leave the discount behind instead. Rendered directly beneath
 * the continue button so both exits are visible at once.
 *
 * The removal action lives here rather than in the coupon row because this notice
 * is the only coupon UI the payment step renders — the row is on cart, checkout
 * and review only. Without it the notice told the shopper to remove a coupon the
 * screen gave them no way to reach.
 */
const CouponBlockedNotice = observer(() => {
  const {cartStore} = useStores();
  const [removing, setRemoving] = useState(false);

  if (!cartStore.couponBlocksCheckout) return null;

  const onRemove = async () => {
    setRemoving(true);
    try {
      await cartStore.updateCoupon(null);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text testID="coupon-blocked-notice" variant="caption" style={styles.notice}>
        You can still check out without the discount — remove the coupon to proceed at full price.
      </Text>
      <Pressable
        testID="coupon-blocked-notice-remove"
        accessibilityRole="button"
        accessibilityLabel="Remove coupon and continue at full price"
        accessibilityState={{disabled: removing}}
        disabled={removing}
        onPress={onRemove}
        style={styles.action}>
        {removing ? (
          <ActivityIndicator size="small" />
        ) : (
          <Text variant="caption" style={styles.actionText}>
            Remove coupon
          </Text>
        )}
      </Pressable>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    gap: 4,
  },
  notice: {},
  action: {
    // 44pt is the iOS HIG and Android touch-target floor; the caption text alone
    // is roughly 16pt tall.
    minHeight: 44,
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  actionText: {
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
});

export default CouponBlockedNotice;
